import React from 'react';
import { Box, Typography } from '@material-ui/core';
import type { ActivitySlot } from '../../../../_models/schedule.model';

interface UtilizationHeatmapProps {
  slots: ActivitySlot[];
}

interface HeatmapData {
  day: string;
  hourSlots: number[];
}

const UtilizationHeatmap: React.FC<UtilizationHeatmapProps> = ({ slots }) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hours = Array.from({ length: 15 }, (_, i) => i + 6); // 6 AM to 8 PM

  // Build heatmap data
  const heatmapData: HeatmapData[] = days.map((day) => ({
    day,
    hourSlots: new Array(15).fill(0),
  }));

  slots.forEach((slot) => {
    if (!slot.date || !slot.startTime) return;

    const date = new Date(slot.date);
    const dayIndex = date.getDay();
    const adjustedDayIndex = dayIndex === 0 ? 6 : dayIndex - 1; // Adjust Sunday from 0 to 6

    const startHour = parseInt(slot.startTime.split(':')[0]);
    const hourIndex = hours.indexOf(startHour);

    if (hourIndex >= 0 && adjustedDayIndex >= 0 && adjustedDayIndex < 7) {
      heatmapData[adjustedDayIndex].hourSlots[hourIndex]++;
    }
  });

  const maxValue = Math.max(...heatmapData.flatMap((d) => d.hourSlots), 1);

  const getColor = (value: number): string => {
    if (value === 0) return '#f7fafc';
    const intensity = value / maxValue;
    if (intensity >= 0.8) return '#2d3748'; // Very high
    if (intensity >= 0.6) return '#4a5568'; // High
    if (intensity >= 0.4) return '#718096'; // Medium
    if (intensity >= 0.2) return '#a0aec0'; // Low
    return '#cbd5e0'; // Very low
  };

  const getTextColor = (value: number): string => {
    const intensity = value / maxValue;
    return intensity >= 0.5 ? 'white' : '#2d3748';
  };

  if (slots.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
          No time-based data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="body2" style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
          Sessions per time slot
        </Typography>
        <Box display="flex" alignItems="center">
          <Typography variant="caption" style={{ color: 'var(--text-muted)', marginRight: 8 }}>
            Less
          </Typography>
          <div style={{ width: '20px', height: '20px', background: 'var(--surface-page)', border: '1px solid var(--border-default)', marginRight: 4 }} />
          <div style={{ width: '20px', height: '20px', background: '#cbd5e0', marginRight: 4 }} />
          <div style={{ width: '20px', height: '20px', background: '#a0aec0', marginRight: 4 }} />
          <div style={{ width: '20px', height: '20px', background: '#718096', marginRight: 4 }} />
          <div style={{ width: '20px', height: '20px', background: '#4a5568', marginRight: 4 }} />
          <div style={{ width: '20px', height: '20px', background: 'var(--surface-inverse)', marginRight: 8 }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            More
          </Typography>
        </Box>
      </Box>

      <div
        style={{
          overflowX: 'auto',
          background: 'white',
          borderRadius: '8px',
          padding: '16px',
        }}
      >
        <div style={{ minWidth: '800px' }}>
          {/* Header row with hours */}
          <div style={{ display: 'flex', marginBottom: '8px' }}>
            <div style={{ width: '100px', flexShrink: 0 }} />
            {hours.map((hour) => (
              <div
                key={hour}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                }}
              >
                {hour}:00
              </div>
            ))}
          </div>

          {/* Data rows */}
          {heatmapData.map((dayData, dayIndex) => (
            <div key={dayData.day} style={{ display: 'flex', marginBottom: '8px' }}>
              <div
                style={{
                  width: '100px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--text-strong)',
                }}
              >
                {dayData.day.substring(0, 3)}
              </div>
              {dayData.hourSlots.map((count, hourIndex) => (
                <div
                  key={`${dayIndex}-${hourIndex}`}
                  style={{
                    flex: 1,
                    background: getColor(count),
                    border: '1px solid var(--border-default)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '40px',
                    margin: '1px',
                    transition: 'all 0.2s ease',
                    cursor: count > 0 ? 'pointer' : 'default',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: getTextColor(count),
                  }}
                  title={`${dayData.day} ${hours[hourIndex]}:00 - ${count} sessions`}
                  onMouseEnter={(e) => {
                    if (count > 0) {
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.zIndex = '10';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.zIndex = '1';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {count > 0 ? count : ''}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <Box mt={3} p={2} style={{ background: 'var(--surface-page)', borderRadius: '8px' }}>
        <Typography variant="body2" style={{ color: 'var(--text-strong)', fontWeight: 600, marginBottom: 8 }}>
          Peak Hours Analysis
        </Typography>
        {(() => {
          const hourTotals = new Array(15).fill(0);
          heatmapData.forEach((dayData) => {
            dayData.hourSlots.forEach((count, index) => {
              hourTotals[index] += count;
            });
          });
          const maxHourIndex = hourTotals.indexOf(Math.max(...hourTotals));
          const minHourIndex = hourTotals.indexOf(Math.min(...hourTotals.filter((v) => v > 0)));

          return (
            <Box display="flex" justifyContent="space-around" flexWrap="wrap">
              <Box style={{ padding: '8px', textAlign: 'center' }}>
                <Typography variant="caption" style={{ color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  Busiest Hour
                </Typography>
                <Typography variant="body1" style={{ color: '#f56565', fontWeight: 700 }}>
                  {hours[maxHourIndex]}:00
                </Typography>
                <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                  {hourTotals[maxHourIndex]} sessions
                </Typography>
              </Box>
              <Box style={{ padding: '8px', textAlign: 'center' }}>
                <Typography variant="caption" style={{ color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  Quietest Hour
                </Typography>
                <Typography variant="body1" style={{ color: '#48bb78', fontWeight: 700 }}>
                  {hours[minHourIndex]}:00
                </Typography>
                <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
                  {hourTotals[minHourIndex]} sessions
                </Typography>
              </Box>
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
};

export default UtilizationHeatmap;
